'use client';

import Image from 'next/image';
import Link from 'next/link';

import { ServiceAllCapsTitle } from './ServiceAllCapsTitle';

export interface PortfolioShowcaseItem {
  date: string;
  image: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
  linkOverrideString?: string;
  linkText: string;
  linkUrl: string;
  patentNumber: string;
  title: string;
}

export interface PortfolioShowcaseProps {
  items: PortfolioShowcaseItem[];
  title?: string;
  variant?: 'dark' | 'light';
}

export function PortfolioShowcase({ items, title = 'PORTFOLIO SHOWCASE', variant = 'dark' }: PortfolioShowcaseProps) {
  if (items.length === 0) return null;

  const isLight = variant === 'light';

  return (
    <section
      className={isLight ? 'bg-white py-[192px] tablet:py-16' : 'bg-navy py-12 tablet:py-16'}
      aria-label="Portfolio showcase"
    >
      <div className="mx-auto w-full max-w-[1328px] px-4 tablet:px-12">
        <div className="pb-6 tablet:pb-10">
          <ServiceAllCapsTitle title={title} />
        </div>
      </div>
      <div className="w-full overflow-x-auto overflow-y-hidden">
        <div
          className="flex justify-start gap-6 pb-6 pl-4 pr-4 tablet:gap-8 tablet:pb-8 tablet:pl-[max(3rem,calc((100vw-1328px)/2+3rem))] tablet:pr-12"
          style={{ width: 'max-content' }}
        >
          {items.map((item, index) => (
            <article
              key={index}
              className="flex w-[280px] min-w-[280px] flex-shrink-0 flex-col tablet:w-[320px] tablet:min-w-[320px]"
            >
              <div className="relative aspect-[3/4] w-full overflow-hidden">
                <Image
                  src={item.image.src}
                  alt={item.image.alt}
                  width={item.image.width}
                  height={item.image.height}
                  className="object-cover object-top"
                  sizes="(max-width: 766px) 280px, 320px"
                />
                {isLight && <div className="pointer-events-none absolute inset-0 z-10 bg-black/20" aria-hidden />}
              </div>
              <div
                className={`flex flex-col gap-2 p-4 font-soehne text-serviceCardBody font-normal leading-[150%] tablet:p-5 ${isLight ? 'text-navy' : 'text-white'}`}
              >
                <h4 className="text-serviceCardTitle font-bold">{item.title}</h4>
                <p>{item.patentNumber}</p>
                <p>{item.date}</p>

                <p className="mt-2 transition-colors">
                  {item.linkOverrideString && item.linkOverrideString.length > 0 ? (
                    item.linkOverrideString
                  ) : (
                    <>
                      See more at
                      <Link
                        href={item.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pl-2 underline transition-colors hover:text-at"
                      >
                        {item.linkText}
                      </Link>
                    </>
                  )}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
